import { useBlockErrorTracking } from '@/hooks/useEvidenceAnalytics';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyPBold } from '@heidi/design-system/typography';
import { AlertCircle, ChevronRight, Loader2, Pin, PinOff } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

interface TemplatePinBlockProps {
  variant: 'pin' | 'unpin';
  status: 'applying' | 'completed' | 'error';
  templateId?: string;
  templateName?: string;
  error?: string;
  className?: string;
  /**
   * Opens the pinned template. Supplied by whoever renders the card, so this
   * component stays unaware of where templates open.
   */
  onOpenTemplate?: (templateId: string) => void;
}

/**
 * Renders `pin_template` / `unpin_template` tool calls in Heidi Work chat.
 */
export const TemplatePinBlock = ({
  variant,
  status,
  templateId,
  templateName,
  error,
  className,
  onOpenTemplate,
}: TemplatePinBlockProps): JSX.Element => {
  const intl = useIntl();
  useBlockErrorTracking(
    variant === 'pin' ? 'pin_template' : 'unpin_template',
    status,
    error
  );
  const Icon = variant === 'pin' ? Pin : PinOff;

  if (status === 'applying') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="template-pin-block"
        data-status="applying"
        data-variant={variant}
      >
        <Icon className="size-5 shrink-0 text-text-tertiary" aria-hidden="true" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="line-clamp-2 text-text-primary">
            {templateName ?? (
              <FormattedMessage defaultMessage="Template" id="3JxaKsUDfq" />
            )}
          </TypographyPBold>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="text-text-tertiary">
              {variant === 'pin' ? (
                <FormattedMessage defaultMessage="Pinning template..." id="vxR0gdJ9lc" />
              ) : (
                <FormattedMessage
                  defaultMessage="Unpinning template..."
                  id="gnHqms6tzG"
                />
              )}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="template-pin-block"
        data-status="error"
        data-variant={variant}
      >
        <AlertCircle
          className="size-5 shrink-0 text-validation-destructive"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="line-clamp-2 text-text-primary">
            {variant === 'pin' ? (
              <FormattedMessage defaultMessage="Couldn't pin template" id="lebBFBeVU4" />
            ) : (
              <FormattedMessage
                defaultMessage="Couldn't unpin template"
                id="kWi8O3zTB/"
              />
            )}
          </TypographyPBold>
          <TypographyCaption className="text-validation-destructive">
            {error ?? (
              <FormattedMessage
                defaultMessage="Something went wrong while updating pinned templates."
                id="9hM+TxonUp"
              />
            )}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  const resolvedName =
    templateName ??
    intl.formatMessage({
      defaultMessage: 'Template',
      id: '3JxaKsUDfq',
    });

  const canOpen = Boolean(templateId);

  const handleOpen = () => {
    if (!templateId) return;
    onOpenTemplate?.(templateId);
  };

  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border border-border bg-surface-accent p-4',
        'transition-all duration-200',
        className
      )}
      data-testid="template-pin-block"
      data-status="completed"
      data-variant={variant}
      data-template-id={templateId}
      data-template-name={resolvedName}
    >
      <Icon className="size-5 shrink-0 text-text-secondary" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <TypographyPBold className="line-clamp-2 text-text-primary">
          {variant === 'pin' ? (
            <FormattedMessage
              defaultMessage="{name} pinned"
              id="tR3qE+n7ic"
              values={{ name: resolvedName }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="{name} unpinned"
              id="FGb0bjZu/E"
              values={{ name: resolvedName }}
            />
          )}
        </TypographyPBold>
        <TypographyCaption className="text-text-tertiary">
          {variant === 'pin' ? (
            <FormattedMessage
              defaultMessage="This template is now in your pinned list for quick access."
              id="Oc69tehdex"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Removed from your pinned templates list."
              id="bTj3n7mqJF"
            />
          )}
        </TypographyCaption>
        {canOpen ? (
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              flush="all"
              className="text-text-secondary"
              onClick={handleOpen}
            >
              <FormattedMessage defaultMessage="Open template" id="zjKk7mRb9I" />
              <ChevronRight className="rtl:-scale-x-100" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
